import React, { useMemo } from 'react';
import type { ProductDisplayType } from '../types/productCreation';
import './StoreProductCard.css';
import '../../../../components/StayUpToDateComponent/UpToDateComponents/HighlightDataSection/HighlightDataSection.css';
import { getShopParityButtonStyle, lightenColor } from '../../../../Utils/StayUpToDate/RenderComponent';
import { useDesign } from '../../../../context/DesignContext';
import { Button } from 'antd';

export interface StoreProductCardProps {
  title: string;
  subtitle?: string;
  thumbnailUrl?: string;
  buttonText: string;
  displayType?: ProductDisplayType;
  onClick?: () => void;
  interactive?: boolean;
  isMobileView?: boolean;
  designTokens?: any;
  /** Label color on the purchase button. Page title color stays on the card. */
  buttonTextColor?: string;
  className?: string;
  style?: React.CSSProperties;
}

const StoreProductCard: React.FC<StoreProductCardProps> = ({
  title,
  subtitle,
  thumbnailUrl,
  buttonText,
  displayType = 'link',
  onClick,
  interactive = false,
  isMobileView = false,
  designTokens,
  buttonTextColor,
  className = '',
  style,
}) => {
  const isClickable = interactive && Boolean(onClick);
  const isThemed = Boolean(style && Object.keys(style).length > 0);
  const {
    templateName,
    secondaryColor,
    tertiaryColor,
    primaryColor,
    forthColor,
    primaryFont,
    secondaryFont,
    fontFamily: contextFontFamily,
  } = useDesign();

  const isDefaultTheme =  String(designTokens?.templateName ?? templateName ?? '').toLowerCase() === 'default';
  const mergedTokens = {
    templateName: designTokens?.templateName ?? templateName,
    secondaryColor: isDefaultTheme ? "#000000" : designTokens?.secondaryColor ?? secondaryColor,
    tertiaryColor: designTokens?.tertiaryColor ?? tertiaryColor,
    primaryColor: designTokens?.primaryColor ?? primaryColor,
    forthColor: designTokens?.forthColor ?? forthColor,
    primaryFontFamily:
      designTokens?.primaryFontFamily || primaryFont || undefined,
    secondaryFontFamily:
      designTokens?.secondaryFontFamily || secondaryFont || undefined,
    fontFamily: designTokens?.fontFamily || contextFontFamily || undefined,
  };
  // Match HighlightDataSection: title → primary font, body/CTA → secondary font.
  const titleFontFamily =
    mergedTokens.primaryFontFamily || mergedTokens.fontFamily;
  const bodyFontFamily =
    mergedTokens.secondaryFontFamily || mergedTokens.fontFamily;
  const effectiveTemplateName = String(mergedTokens.templateName || '').toLowerCase();
  
  const buttonOverrideClass = isDefaultTheme ? 'highlight-btn-override' : '';

  const shopParity = useMemo(
    () =>
      getShopParityButtonStyle({
        designTokens: mergedTokens,
        contextButtonBackground: isDefaultTheme ? "#000000" : mergedTokens.forthColor,
      }),
    [
      mergedTokens.templateName,
      mergedTokens.secondaryColor,
      mergedTokens.tertiaryColor,
      mergedTokens.primaryColor,
      mergedTokens.forthColor,
    ]
  );

  const mediaClassName =
    displayType === 'full_view'
      ? `store-product-card__media`
      : 'store-product-card__media';

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isClickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  // Public page: hover parity with ProductShopButton. Dashboard: no hover class.
  const buttonClassName = [
    'store-product-card__btn',
    'highlight-shop-button',
    buttonOverrideClass,
    interactive
      ? 'custom-button-with-hover my-store-product-btn follower-shop-parity-btn store-product-card__btn--public'
      : 'store-product-card__btn--dashboard',
    isMobileView && displayType === 'link' ? 'highlight-shop-button-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const buttonLabelColor = buttonTextColor || shopParity.color;
  const buttonStyle: React.CSSProperties = {
    ...shopParity.structural,
    color: buttonLabelColor,
    fontFamily: bodyFontFamily,
    // ['--store-preview-btn-bg' as string]: shopParity.backgroundColor,
    ['--store-preview-btn-text' as string]: buttonLabelColor,
    ['--follower-cta-bg' as string]: shopParity.backgroundColor,
    ['--follower-cta-fg' as string]: isDefaultTheme ? "#ffffff" : buttonLabelColor,
    borderRadius:"6px",
    ...(interactive
      ? {
          ['--hover-bg' as string]: isDefaultTheme ? lightenColor("#000000", 20) : shopParity.hoverBg,
          cursor: 'pointer',
        }
      : {
          cursor: 'default',
        }),
  };

  const titleStyle: React.CSSProperties | undefined = titleFontFamily
    ? { fontFamily: titleFontFamily }
    : undefined;
  const subtitleStyle: React.CSSProperties | undefined = bodyFontFamily
    ? { fontFamily: bodyFontFamily }
    : undefined;

  const renderButton = () => (
    <Button
      type={interactive ? 'primary' : 'default'}
      className={buttonClassName}
      style={buttonStyle}
      disabled={!interactive}
      tabIndex={interactive ? 0 : -1}
      aria-disabled={!interactive}
    >
      {buttonText || 'Learn More'}
    </Button>
  );

  const renderMedia = () => (
    <div className={mediaClassName}>
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt="" loading="lazy" decoding="async" />
      ) : (
        <div className="store-product-card__placeholder" />
      )}
    </div>
  );

  const renderLinkView = () => (
    <>
      <div className="store-product-card__left highlight-data-section store-product-card__highlight-layout">
        {renderMedia()}
        <div className="highlight-details store-product-card__details">
          <p
            className="highlight-name store-product-card__title"
            style={titleStyle}
          >
            {title || 'Product title'}
          </p>
          {subtitle && (
            <p className="store-product-card__subtitle" style={subtitleStyle}>
              {subtitle || 'Product subtitle'}
            </p>
          )}
        </div>
      </div>
      <div className="store-product-card__right">{renderButton()}</div>
    </>
  );

  const renderFullView = () => (
    <>
      {renderMedia()}
      <div className="store-product-card__body">
        <p className="store-product-card__title" style={titleStyle}>
          {title || 'Product title'}
        </p>
        {subtitle && (
          <p className="store-product-card__subtitle" style={subtitleStyle}>
            {subtitle}
          </p>
        )}
        {renderButton()}
      </div>
    </>
  );

  return (
    <div
      className={`store-product-card store-product-card--${displayType}${
        isClickable ? ' store-product-card--interactive' : ''
      }${isThemed ? ' store-product-card--themed' : ''}${
        isMobileView ? ' store-product-card--mobile' : ''
      }${className ? ` ${className}` : ''}`}
      style={style}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {displayType === 'link' ? renderLinkView() : renderFullView()}
    </div>
  );
};


export default StoreProductCard;
